import Brand from './Brand.js';
import Utils from '../Utils.js';

/*
 * Theme
 *
 * The design system for the consumer-facing marketing surface, hero,
 * products, coverage quiz, quote cart. Ported from the storefront so the two
 * properties look like one company's work, with every brand colour resolved
 * per dealership through Brand.js instead of being fixed.
 *
 * This sits ALONGSIDE styles/Appearance.js and does not replace it. Appearance
 * is the legacy panel/layer system and is still what support, troubleshooting,
 * careers, trainee signup, GRACI, and the legal views draw with. The two have
 * colliding key names (colors, fonts, textStyles, styles, boxShadow) and
 * incompatible values, so they must not be merged. New marketing components
 * import Theme; nothing else should.
 *
 * Where the storefront hard coded its own blue, this reads Brand:
 *
 *   colors.primary()      accent for text, borders, tints, contrast safe
 *   colors.primaryFill()  the dealership's colour untouched, for filled surfaces
 *   colors.onPrimary()    foreground that reads on top of primaryFill()
 *   colors.brandSurface() the dealership's secondary colour, as a ground
 *
 * The distinction between primary() and primaryFill() is the whole point of
 * Brand.js, see the note at the top of that file before collapsing them.
 *
 * Unlike Appearance, this is a light-only system. The marketing surface is
 * pinned to a white ground regardless of the visitor's colour scheme; the
 * legacy panels keep their dark mode.
 */

const Theme = {
  colors: {
    // Accent, used only for links, labels, and primary CTAs, never as a
    // surface. Contrast corrected so a pale dealership colour stays legible.
    primary: () => Brand.ink(),
    // Hover partner for primary(), derived rather than supplied.
    secondary: () => Brand.primaryHover(),
    // Filled controls: the dealership's colour deepened just enough that
    // white text clears AA on it, and white to sit on top. See the note on
    // readableFill in Brand.js for why this deepens the ground rather than
    // switching the text to black.
    primaryFill: () => Brand.fill(),
    onPrimary: () => Brand.onFill(),
    // The colour exactly as the dealership authored it, for places where
    // nothing sits on top, a rule, a dot, a glow.
    primaryRaw: () => Brand.primary(),
    // TEXT and icons on the near-black bands. A deep navy brand is
    // invisible on dark() otherwise, use this, not primary(), for any
    // lettering inside a dark section.
    primaryOnDark: () => Brand.inkOnDark(),
    onPrimaryOnDark: () => Brand.onInkOnDark(),
    // FILLED controls on the near-black bands: visible against the band and
    // still carrying white, so a button reads the same everywhere on the
    // site rather than flipping to dark lettering once the ground changes.
    primaryFillOnDark: () => Brand.fillOnDark(),
    // The dealership's secondary colour. Historically the header band.
    brandSurface: () => Brand.surface(),
    onBrandSurface: () => Brand.onSurface(),
    // Near-black text / dark surfaces
    dark: () => '#1D1D1F',
    darkGrey: '#1D1D1F',
    grey: '#6E6E73',
    lightGrey: '#86868B',
    lighterGrey: '#E8E8ED',
    // Grounds
    background: () => '#FFFFFF',
    white: '#FFFFFF',
    offWhite: '#F5F5F7',
    // section grey + product image stage
    text: () => '#1D1D1F',
    subText: () => '#6E6E73',
    border: () => '#D2D2D7',
    divider: () => 'rgba(0,0,0,0.08)',
    success: '#2FA84F',
    error: '#E30000',
    warning: '#f53d00',
    transparent: 'rgba(0,0,0,0)'
  },
  // Translucent wash of any colour, for chips, tints, and focus rings.
  // Defaults to the dealership accent when no colour is supplied.
  tint: (alpha, color) => Brand.tint(alpha, color),
  fonts: {
    // System stack → real San Francisco on Apple devices; clean sans
    // elsewhere. No serif: the display face and body face are one
    // considered sans.
    //
    // index.php also loads Gabarito, Outfit, and Poppins for the legacy
    // panels. The marketing surface deliberately does not use them, the
    // storefront's typographic voice is the system face.
    heading: "-apple-system, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    body: "-apple-system, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
  },
  textStyles: {
    heroHeading: props => ({
      fontFamily: Theme.fonts.heading,
      fontWeight: 600,
      fontSize: Utils.isMobile() ? 44 : 76,
      lineHeight: 1.05,
      color: Theme.colors.text(),
      letterSpacing: '-0.03em',
      ...props
    }),
    sectionHeading: props => ({
      fontFamily: Theme.fonts.heading,
      fontWeight: 600,
      fontSize: Utils.isMobile() ? 30 : 44,
      lineHeight: 1.08,
      color: Theme.colors.text(),
      letterSpacing: '-0.022em',
      textAlign: 'center',
      ...props
    }),
    sectionSubheading: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 400,
      fontSize: Utils.isMobile() ? 14 : 16,
      lineHeight: 1.6,
      color: Theme.colors.subText(),
      textAlign: 'center',
      maxWidth: 640,
      ...props
    }),
    heading: props => ({
      fontFamily: Theme.fonts.heading,
      fontWeight: 600,
      fontSize: Utils.isMobile() ? 24 : 32,
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      color: Theme.colors.text(),
      ...props
    }),
    subHeading: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 600,
      fontSize: Utils.isMobile() ? 18 : 22,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      color: Theme.colors.text(),
      ...props
    }),
    body: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 400,
      fontSize: Utils.isMobile() ? 14 : 16,
      lineHeight: 1.6,
      color: Theme.colors.subText(),
      ...props
    }),
    caption: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 500,
      fontSize: 13,
      lineHeight: 1.4,
      color: Theme.colors.lightGrey,
      ...props
    }),
    label: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 600,
      fontSize: 12,
      lineHeight: 1.3,
      color: Theme.colors.primary(),
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      ...props
    }),
    navLink: props => ({
      fontFamily: Theme.fonts.body,
      fontWeight: 500,
      fontSize: 15,
      color: Theme.colors.text(),
      textDecoration: 'none',
      cursor: 'pointer',
      ...props
    }),
    stat: props => ({
      fontFamily: Theme.fonts.heading,
      fontWeight: 600,
      fontSize: Utils.isMobile() ? 40 : 56,
      lineHeight: 1.0,
      letterSpacing: '-0.03em',
      fontVariantNumeric: 'tabular-nums',
      color: Theme.colors.text(),
      ...props
    })
  },
  styles: {
    card: () => ({
      backgroundColor: Theme.colors.white,
      borderRadius: 16,
      overflow: 'hidden',
      border: `1px solid ${Theme.colors.border()}`,
      transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    }),
    section: () => ({
      width: '100%',
      maxWidth: 1200,
      margin: '0 auto',
      padding: Utils.isMobile() ? '40px 20px' : '80px 40px'
    }),
    button: {
      // Filled with the dealership's colour as authored, so the button
      // reads as their brand, with a foreground picked to sit on it.
      primary: () => ({
        backgroundColor: Theme.colors.primaryFill(),
        color: Theme.colors.onPrimary(),
        border: 'none',
        borderRadius: 30,
        padding: '14px 32px',
        fontFamily: Theme.fonts.body,
        fontWeight: 600,
        fontSize: 15,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        // A stretched flex row cannot resize an inline-flex child, so
        // the buttons switch to flex on a phone and let the row that
        // holds them decide the width. See views/steps/StepFrame.js.
        display: Utils.isMobile() ? 'flex' : 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8
      }),
      secondary: () => ({
        backgroundColor: Theme.colors.dark(),
        color: Theme.colors.white,
        border: 'none',
        borderRadius: 30,
        padding: '14px 32px',
        fontFamily: Theme.fonts.body,
        fontWeight: 600,
        fontSize: 15,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        // A stretched flex row cannot resize an inline-flex child, so
        // the buttons switch to flex on a phone and let the row that
        // holds them decide the width. See views/steps/StepFrame.js.
        display: Utils.isMobile() ? 'flex' : 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8
      }),
      outline: () => ({
        backgroundColor: 'transparent',
        color: Theme.colors.text(),
        border: `2px solid ${Theme.colors.text()}`,
        borderRadius: 30,
        padding: '12px 30px',
        fontFamily: Theme.fonts.body,
        fontWeight: 600,
        fontSize: 15,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        // A stretched flex row cannot resize an inline-flex child, so
        // the buttons switch to flex on a phone and let the row that
        // holds them decide the width. See views/steps/StepFrame.js.
        display: Utils.isMobile() ? 'flex' : 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8
      })
    },
    input: () => ({
      fontFamily: Theme.fonts.body,
      fontSize: 15,
      padding: '14px 20px',
      borderRadius: 30,
      border: `1px solid ${Theme.colors.border()}`,
      backgroundColor: Theme.colors.white,
      color: Theme.colors.text(),
      outline: 'none',
      transition: 'border-color 0.3s ease',
      width: '100%'
    })
  },
  // Shared section rhythm, every section uses these so widths, padding, and
  // vertical spacing line up instead of each section choosing its own.
  layout: {
    sectionPadding: () => Utils.isMobile() ? '64px 20px' : '96px 40px',
    maxWidth: 1200,
    // standard content width (grids, media, 2-col)
    readableWidth: 820,
    // long-form text / accordions / single column

    // Fixed-header geometry. The header is pinned to headerHeight() so it
    // cannot resize with its contents. Anything drawn beneath the header
    // measures from the same numbers, so they must be changed together and
    // are therefore defined together. These are border-box values, so
    // headerHeight() includes the vertical half of headerPadding().
    headerHeight: () => Utils.isMobile() ? 72 : 76,
    headerPadding: () => Utils.isMobile() ? '16px 20px' : '18px 40px',
    // Top offset for content that starts below the fixed header, with a
    // little extra breathing room on mobile where the bar sits tighter.
    headerOffset: () => Theme.layout.headerHeight() + (Utils.isMobile() ? 12 : 0)
  },
  boxShadow: {
    card: '0 4px 24px rgba(0,0,0,0.05)',
    cardHover: '0 18px 48px rgba(0,0,0,0.10)',
    // A function rather than a constant because the glow is tinted with
    // the dealership's accent; the others are brand independent.
    button: () => `0 6px 18px ${Brand.tint(0.22, Brand.primary())}`,
    nav: '0 1px 0 rgba(0,0,0,0.05)'
  }
};
export default Theme;